import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired or invalid)
    if (error.response && error.response.status === 401) {
      // Clear auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      
      // Redirect to login page if not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    // Handle 403 Forbidden errors (insufficient permissions)
    if (error.response && error.response.status === 403) {
      console.error('Permission denied:', error.response.data);
    }
    
    return Promise.reject(error);
  }
);

// API service with CRUD methods
const apiService = {
  // GET request
  get: async (endpoint, params = {}) => {
    try {
      const response = await apiClient.get(endpoint, { params });
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  // POST request
  post: async (endpoint, data = {}) => {
    try {
      const response = await apiClient.post(endpoint, data);
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  // PUT request
  put: async (endpoint, data = {}) => {
    try {
      const response = await apiClient.put(endpoint, data);
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  // PATCH request
  patch: async (endpoint, data = {}) => {
    try {
      const response = await apiClient.patch(endpoint, data);
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  // DELETE request
  delete: async (endpoint, params = {}) => {
    try {
      const response = await apiClient.delete(endpoint, { params });
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  // Upload file(s)
  upload: async (endpoint, formData, onUploadProgress = null) => {
    try {
      const response = await apiClient.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onUploadProgress 
          ? (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onUploadProgress(percentCompleted);
            }
          : undefined,
      });
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  // Download file
  download: async (endpoint, params = {}, filename = null) => {
    try {
      const response = await apiClient.get(endpoint, { 
        params,
        responseType: 'blob' 
      });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Extract filename from Content-Disposition header if not provided
      let downloadFilename = filename;
      if (!downloadFilename) {
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch.length === 2) {
            downloadFilename = filenameMatch[1];
          }
        } else {
          downloadFilename = 'download';
        }
      }
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      return true;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  // Auth functions
  auth: {
    login: async (credentials) => {
      try {
        const response = await apiClient.post('/auth/login', credentials);
        
        if (response.data.token) {
          localStorage.setItem('authToken', response.data.token);
          
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
        }
        
        return response.data;
      } catch (error) {
        handleRequestError(error);
        throw error;
      }
    },
    
    logout: () => {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    },
    
    getUser: () => {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    },
    
    isAuthenticated: () => {
      return !!localStorage.getItem('authToken');
    }
  },
  
  // E-commerce specific functions
  createOrder: async (orderData) => {
    try {
      const response = await apiClient.post('/orders', orderData);
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  getOrder: async (orderId) => {
    try {
      const response = await apiClient.get(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  },
  
  getOrders: async (params = {}) => {
    try {
      const response = await apiClient.get('/orders', { params });
      return response.data;
    } catch (error) {
      handleRequestError(error);
      throw error;
    }
  }
};

// Error handling helper
const handleRequestError = (error) => {
  if (error.response) {
    // Server responded with an error status code
    console.error('API Error Response:', error.response.data);
    console.error('Status:', error.response.status);
  } else if (error.request) {
    // No response received
    console.error('API Request Error - No response received:', error.request);
  } else {
    // Error in setting up request
    console.error('API Error:', error.message);
  }
};

// Export createOrder as a named export
export const createOrder = apiService.createOrder;

// Export apiService as default
export default apiService;